import { Injectable, Inject, PLATFORM_ID, Optional } from '@angular/core';
import { Meta, Title } from '@angular/platform-browser';
import { TenantConfService } from './tenant-conf.service';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { InitialDataService } from './initialData.service';
import { DOCUMENT, isPlatformServer, TitleCasePipe } from '@angular/common';
import { REQUEST } from '../../express.tokens';
import { environment } from 'src/environments/environment';
import { Request } from 'express';

@Injectable({
  providedIn: 'root'
})
export class MetadataService {
  constructor(
    @Inject(DOCUMENT) private document: any,
    // tslint:disable-next-line: ban-types
    @Inject(PLATFORM_ID) private platformId: Object,
    @Optional() @Inject(REQUEST) protected request: Request,
    private tenantService: TenantConfService,
    private title: Title,
    private meta: Meta,
    private titleCasePipe: TitleCasePipe,
    private sanitizer: DomSanitizer,
    private initialDataService: InitialDataService
  ) {}

  setStandardMetadata(title, description?) {
    const pageTitle = `${title} | ${this.initialDataService.seoTitle.getValue()}`;
    const pageDescription =
      description !== undefined ? description : this.getTextWithoutHTMLTags(this.initialDataService.seoDescription.getValue());
    this.title.setTitle(pageTitle);
    this.meta.updateTag({
      property: 'description',
      content: pageDescription
    });
    this.setOgMetadata(pageTitle, pageDescription, null);
    this.setTwitterMetadata(pageTitle, pageDescription, null);
  }

  setMetadata(title, description, image) {
    if (!title) {
      return;
    }
    this.title.setTitle(title);
    this.meta.updateTag({ property: 'description', content: this.getTextWithoutHTMLTags(description) });
    this.setOgMetadata(title, description, image);
    this.setTwitterMetadata(title, description, image);
  }

  setOgMetadata(title, description, image) {
    this.meta.updateTag({ property: 'og:title', content: title });
    this.meta.updateTag({ property: 'og:site_name', content: this.tenantService.getTenantFromUrl() });
    this.meta.updateTag({ property: 'og:type', content: 'website' });
    this.meta.updateTag({ property: 'og:description', content: this.getTextWithoutHTMLTags(description) });
    this.meta.updateTag({ property: 'og:url', content: this.document.location.href });

    if (!image) {
      this.meta.removeTag("name='og:image'");
      this.meta.removeTag("name='og:image:width'");
      this.meta.removeTag("name='og:image:height'");
      this.meta.removeTag("name='og:image:type'");
      return;
    }

    this.meta.updateTag({ property: 'og:image:width', content: '300' });
    this.meta.updateTag({ property: 'og:image:height', content: '300' });

    this.meta.updateTag({
      property: 'og:image',
      content: image
    });

    this.meta.updateTag({
      property: 'og:image:type',
      content: 'image/png'
    });
  }

  setTwitterMetadata(title, description, image) {
    this.meta.updateTag({
      property: 'twitter:title',
      content: title
    });
    this.meta.updateTag({ property: 'twitter:description', content: this.getTextWithoutHTMLTags(description) });

    if (!image) {
      this.meta.removeTag("name='twitter:image'");
      return;
    }

    this.meta.updateTag({
      property: 'twitter:card',
      content: 'summary_large_image'
    });

    this.meta.updateTag({
      property: 'twitter:image',
      content: image
    });
  }

  getEventJSONLD(title, startDate, endDate, description, image, url, placeName, placeUrl) {
    const json = {
      '@context': 'http://schema.org',
      '@type': 'Event',
      url: this.getCurrentUrl(),
      name: title,
      startDate,
      endDate,
      eventAttendanceMode: 'https://schema.org/OfflineEventAttendanceMode',
      eventStatus: 'https://schema.org/EventScheduled',
      location: {
        '@type': 'Place',
        name: placeName,
        address: {
          '@type': 'PostalAddress',
          streetAddress: this.initialDataService.address.getValue().direction,
          addressLocality: this.initialDataService.address.getValue().location
        }
      },
      image: [image],
      description,
      organizer: {
        '@type': 'Organization',
        name: this.tenantService.getTenantFromUrl(),
        url: placeUrl
      }
    };
    this.setJsonData(json);
    return this.getSafeHTML(json);
  }

  getFilmListJSONLD(films: Film[]) {
    const json = {
      '@context': 'https://schema.org',
      '@type': 'ItemList',
      itemListElement: []
    };

    const uniqueFilms = this.filterUniqueFilms(films);
    uniqueFilms.forEach(film => {
      const filmJson = {
        '@type': 'ListItem',
        position: json.itemListElement.length + 1,
        item: {
          '@type': 'Movie',
          url: `${this.getCurrentUrl()}#${film.title}`,
          name: film.Title,
          position: json.itemListElement.length + 1,
          image: film.image,
          dateCreated: film.releaseYear,
          director: {
            '@type': 'Person',
            name: film.director
          }
        }
      };
      json.itemListElement.push(filmJson);
    });
    this.setJsonData(json);
    return this.getSafeHTML(json);
  }

  getShopJSONLD(shop: any) {
    const json = {
      '@context': 'https://schema.org',
      '@type': 'Store',
      image: [shop.data.header, shop.data.logo],
      '@id': shop.data.name,
      name: shop.data.name,
      url: this.getCurrentUrl(),
      telephone: shop.phone
    };
    return this.getSafeHTML(json);
  }

  getLogoJSONLD(logo: string) {
    const json = {
      '@context': 'https://schema.org',
      '@type': 'Organization',
      url: this.getCurrentUrl(),
      logo
    };
    this.setJsonData(json);
    return this.getSafeHTML(json);
  }

  private setJsonData(json) {
    if (document) {
      const script = document.createElement('script');
      script.setAttribute('type', 'application/ld+json');
      script.textContent = JSON.stringify(json, null, 2).replace(/\//g, '\\/');
      document.head.appendChild(script);
    }
  }

  private getSafeHTML(value: {}) {
    // If value convert to JSON and escape / to prevent script tag in JSON
    const json = value ? JSON.stringify(value, null, 2).replace(/\//g, '\\/') : '';
    const html = `<script type="application/ld+json">${json}</script>`;
    return this.sanitizer.bypassSecurityTrustHtml(html);
  }

  private filterUniqueFilms(films) {
    return films.filter((film, index, self) => index === self.findIndex(t => t.title === film.title));
  }

  private getFilmURLName(title) {
    return title.replace(/[^a-zA-Z0-9]/g, '');
  }

  private transformToTitleCase(value: string) {
    return this.titleCasePipe.transform(value);
  }

  private getTextWithoutHTMLTags(text) {
    return text === undefined ? '' : text.replace(/(<([^>]+)>)/gi, '');
  }

  private getCurrentUrl() {
    return isPlatformServer(this.platformId) ? this.request.headers.referer || 'localhost:4200' : window.location.href;
  }
}

interface Film {
  Title: string;
  ReleaseYear: string;
  Directors: string;
}
